import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useI18n } from "../i18n/I18nContext";
import { NAV_LINKS } from "../lib/brand";

export default function SiteHeader({ variant = "dark", floating = false }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const dark = variant === "dark";

  const wrap = floating
    ? "absolute top-0 left-0 right-0 z-30"
    : dark
      ? "sticky top-0 z-30 bg-charcoal-900 dark-surface"
      : "sticky top-0 z-30 bg-white border-b border-warmgray-200";

  const linkBase = "text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors";
  const linkInactive = dark ? "text-white/70 hover:text-white" : "text-warmgray-700 hover:text-charcoal";
  const linkActive = dark ? "text-gold-500" : "text-emerald2";

  return (
    <header className={wrap}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <Logo dark={dark} />
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}>
              {lang === "en" ? l.en : l.vi}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LanguageToggle variant={dark ? "dark" : "light"} className="hidden md:flex" />
          {/* Phase 8.19 — Organizer sign-in button in the top-right nav.
              Primary use case: organizers coming from an event page to log
              into their dashboard. Master-admin sign-in stays in the footer
              (rarer flow, one link is enough). */}
          <Link
            to="/organizer/login"
            data-testid="nav-organizer-signin"
            className={`hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors ${
              dark
                ? "bg-gold-500 text-charcoal hover:bg-gold-400"
                : "bg-charcoal text-white hover:bg-charcoal-800"
            }`}
          >
            <LogIn className="w-3.5 h-3.5" />
            {lang === "en" ? "Sign in" : "Đăng nhập"}
          </Link>
          <button type="button" onClick={() => setOpen(true)} data-testid="mobile-menu-open"
            className={`lg:hidden p-2 rounded-full ${dark ? "text-white" : "text-charcoal"}`}>
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-charcoal/60 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-[82%] max-w-sm bg-charcoal-900 dark-surface text-white p-6"
            onClick={(e) => e.stopPropagation()} data-testid="mobile-menu-sheet">
            <div className="flex items-center justify-between">
              <Logo dark variant="full" />
              <button type="button" onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="mt-8 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-xl text-base font-medium text-white/80 hover:text-white hover:bg-white/5">
                  {lang === "en" ? l.en : l.vi}
                </Link>
              ))}
              <Link
                to="/organizer/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-organizer-signin"
                className="mt-3 inline-flex items-center gap-2 justify-center bg-gold-500 text-charcoal font-semibold rounded-full px-4 py-3 hover:bg-gold-400"
              >
                <LogIn className="w-4 h-4" />
                {lang === "en" ? "Sign in" : "Đăng nhập"}
              </Link>
            </nav>
            <div className="mt-6"><LanguageToggle variant="dark" /></div>
          </div>
        </div>
      )}
    </header>
  );
}
